import { useEffect, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase.js'

const navItems = [
  { to: '/admin/dashboard', label: 'Tableau de bord', icon: '📊' },
  { to: '/admin/themes',    label: 'Thèmes',           icon: '🎨' },
  { to: '/admin/musiques',  label: 'Musiques',         icon: '🎵' },
  { to: '/admin/decks',     label: 'Quiz',             icon: '📚' },
  { to: '/admin/users',     label: 'Utilisateurs',     icon: '👥' },
  { to: '/admin/feedbacks', label: 'Avis',             icon: '💬' },
]

export default function AdminLayout({ children }) {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setUser(session.user)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) navigate('/login', { replace: true })
      else setUser(session.user)
    })
    return () => subscription.unsubscribe()
  }, [navigate])

  async function handleLogout() {
    setMenuOpen(false)
    await supabase.auth.signOut()
    navigate('/login')
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-900">

      {/* ── En-tête ───────────────────────────────────────────── */}
      <header className="bg-gray-900/95 backdrop-blur-sm border-b border-white/10 text-white px-4 py-3 flex items-center justify-between gap-4 shrink-0">
        {/* Logo */}
        <div className="flex items-center gap-2 text-xl">
          <img src="/android-chrome-192x192.png" alt="KartQuiz" className="w-8 h-8 rounded-lg shrink-0" />
          <span className="font-brand text-white">KartQuiz</span>
          <span className="text-white/40 font-normal text-sm ml-1">Admin</span>
        </div>

        {/* Actions desktop (md+) */}
        <div className="hidden md:flex items-center gap-3 text-sm">
          <span className="text-white/50">{user?.email}</span>
          <NavLink
            to="/profile"
            className="bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition min-h-[36px] flex items-center gap-1.5"
          >
            👤 Mon profil
          </NavLink>
          <NavLink
            to="/home"
            className="bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition min-h-[36px] flex items-center"
          >
            🏠 Accueil
          </NavLink>
          <button
            onClick={handleLogout}
            className="bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition min-h-[36px]"
          >
            Déconnexion
          </button>
        </div>

        {/* Hamburger mobile (< md) */}
        <button
          onClick={() => setMenuOpen(true)}
          className="md:hidden text-white/70 hover:text-white p-2 rounded-lg hover:bg-white/10 transition"
          aria-label="Menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </header>

      {/* ── Drawer mobile ─────────────────────────────────────── */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMenuOpen(false)}
          />
          {/* Panel */}
          <div className="absolute inset-y-0 left-0 w-64 bg-gray-800 flex flex-col shadow-2xl">
            {/* En-tête drawer */}
            <div className="flex items-center justify-between px-4 py-4 bg-gray-900/95 border-b border-white/10">
              <div className="flex items-center gap-2">
                <img src="/android-chrome-192x192.png" alt="KartQuiz" className="w-7 h-7 rounded-lg" />
                <span className="font-brand text-white">KartQuiz</span>
                <span className="text-white/40 text-sm font-normal">Admin</span>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                className="text-white/50 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
                aria-label="Fermer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Email */}
            <div className="px-4 py-3 border-b border-white/10 text-sm text-white/40 truncate">{user?.email}</div>

            {/* Nav items */}
            <nav className="flex-1 flex flex-col pt-2">
              {navItems.map(({ to, label, icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 min-h-[44px] text-sm font-medium transition ${
                      isActive
                        ? 'bg-blue-500/20 text-blue-300 border-r-2 border-blue-400'
                        : 'text-white/60 hover:bg-white/10 hover:text-white'
                    }`
                  }
                >
                  <span className="text-lg">{icon}</span>
                  <span>{label}</span>
                </NavLink>
              ))}
            </nav>

            {/* Bas drawer — Mon profil + Accueil + Déconnexion */}
            <div className="border-t border-white/10 p-3 flex flex-col gap-1">
              <NavLink
                to="/profile"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 min-h-[44px] text-sm font-medium text-white/60 hover:bg-white/10 hover:text-white rounded-xl transition"
              >
                <span className="text-lg">👤</span>
                <span>Mon profil</span>
              </NavLink>
              <NavLink
                to="/home"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 min-h-[44px] text-sm font-medium text-white/60 hover:bg-white/10 hover:text-white rounded-xl transition"
              >
                <span className="text-lg">🏠</span>
                <span>Accueil</span>
              </NavLink>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 min-h-[44px] text-sm font-medium text-red-400 hover:bg-red-500/20 rounded-xl transition w-full text-left"
              >
                <span className="text-lg">🚪</span>
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* ── Sidebar desktop (md+) ─────────────────────────────── */}
        <nav className="hidden md:flex w-48 flex-col bg-gray-800/30 border-r border-white/10 shrink-0 pt-4 overflow-y-auto">
          {navItems.map(({ to, label, icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 min-h-[44px] text-sm font-medium transition ${
                  isActive
                    ? 'bg-blue-500/20 text-blue-300 border-r-2 border-blue-400'
                    : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`
              }
            >
              <span>{icon}</span>
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* ── Contenu principal ─────────────────────────────────── */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">{children}</main>
      </div>
    </div>
  )
}
